/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Invoice API Service
 * CRUD + status transitions + payments + PDF download
 */

import apiClient from '@/services/api/client';
import { get, post, put } from '@/services/api/client';
import type { ApiResponse } from '@/services/api/types';
import type { PagedResult } from '../types/product';
import type {
  InvoiceListItem,
  InvoiceDetail,
  InvoiceStats,
  CreateInvoiceRequest,
  CreateFromOrderRequest,
  RecordPaymentRequest,
  PaymentResponse,
} from '../types/invoice';

const BASE = '/venta/invoices';

export interface InvoiceListParams {
  search?: string;
  status?: string;
  partnerId?: string;
  page?: number;
  size?: number;
  sort?: string;
  direction?: string;
}

export async function getInvoices(params: InvoiceListParams = {}): Promise<PagedResult<InvoiceListItem>> {
  const queryParams: Record<string, unknown> = {};
  if (params.search) queryParams.search = params.search;
  if (params.status) queryParams.status = params.status;
  if (params.partnerId) queryParams.partnerId = params.partnerId;
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;
  if (params.sort) queryParams.sort = params.sort;
  if (params.direction) queryParams.direction = params.direction;

  const response = await get<ApiResponse<PagedResult<InvoiceListItem>>>(BASE, queryParams);
  return response.data;
}

export async function getInvoice(id: string): Promise<InvoiceDetail> {
  const response = await get<ApiResponse<InvoiceDetail>>(`${BASE}/${id}`);
  return response.data;
}

export async function getInvoiceStats(): Promise<InvoiceStats> {
  const response = await get<ApiResponse<InvoiceStats>>(`${BASE}/stats`);
  return response.data;
}

export async function createInvoice(data: CreateInvoiceRequest): Promise<InvoiceDetail> {
  const response = await post<ApiResponse<InvoiceDetail>>(BASE, data);
  return response.data;
}

export async function updateInvoice(id: string, data: CreateInvoiceRequest): Promise<InvoiceDetail> {
  const response = await put<ApiResponse<InvoiceDetail>>(`${BASE}/${id}`, data);
  return response.data;
}

export async function createFromOrder(data: CreateFromOrderRequest): Promise<InvoiceDetail> {
  const response = await post<ApiResponse<InvoiceDetail>>(`${BASE}/from-order`, data);
  return response.data;
}

export async function sendInvoice(id: string): Promise<InvoiceDetail> {
  const response = await post<ApiResponse<InvoiceDetail>>(`${BASE}/${id}/send`);
  return response.data;
}

export async function recordPayment(id: string, data: RecordPaymentRequest): Promise<InvoiceDetail> {
  const response = await post<ApiResponse<InvoiceDetail>>(`${BASE}/${id}/record-payment`, data);
  return response.data;
}

export async function getPayments(id: string): Promise<PaymentResponse[]> {
  const response = await get<ApiResponse<PaymentResponse[]>>(`${BASE}/${id}/payments`);
  return response.data;
}

export async function voidInvoice(id: string): Promise<InvoiceDetail> {
  const response = await post<ApiResponse<InvoiceDetail>>(`${BASE}/${id}/void`);
  return response.data;
}

export async function cancelInvoice(id: string, reason?: string): Promise<InvoiceDetail> {
  const response = await post<ApiResponse<InvoiceDetail>>(`${BASE}/${id}/cancel`, reason ? { reason } : {});
  return response.data;
}

export function getInvoicePdfUrl(id: string, template?: string, lang?: string): string {
  const params = new URLSearchParams();
  if (template) params.set('template', template);
  if (lang) params.set('lang', lang);
  const qs = params.toString();
  return `/api/v1${BASE}/${id}/pdf${qs ? '?' + qs : ''}`;
}

/** Download invoice PDF as blob */
export async function downloadInvoicePdf(
  id: string,
  template?: string,
  lang: string = 'en'
): Promise<Blob> {
  const params: Record<string, string> = { lang };
  if (template) params.template = template;
  const response = await apiClient.get(`${BASE}/${id}/pdf`, {
    params,
    responseType: 'blob',
  });
  return response.data;
}

export const invoiceService = {
  getInvoices,
  getInvoice,
  getInvoiceStats,
  createInvoice,
  updateInvoice,
  createFromOrder,
  sendInvoice,
  recordPayment,
  getPayments,
  voidInvoice,
  cancelInvoice,
  getInvoicePdfUrl,
  downloadInvoicePdf,
};
